import {
  CalcDefinition,
  ReactionTemplate,
  ChemConfig,
  BomFieldDefinition,
} from './types';

/**
 * Chemistry Plugin Configuration
 * 
 * This file contains:
 * 1. Calculation definitions for API, FD, and ADL experiments
 * 2. Reaction templates that define BOM fields and enabled calculations
 * 
 * Phase 2: Data-driven calculations and templates
 * Phase 4: Real chemistry formulas with evaluator pattern
 * 
 * NOTE: Phase 4 calculations use real evaluators with proper formulas.
 * Legacy formula-based calculations are kept for backward compatibility.
 */

// ============================================================================
// CALCULATION DEFINITIONS
// ============================================================================

/**
 * Collection of all available calculations
 * Each calculation has:
 * - Scope: Which experiment types (API/FD/ADL) can use it
 * - Formula: JavaScript expression for evaluation
 * - Inputs: Manual or BOM-derived values
 */
export const calculations: CalcDefinition[] = [
  // --------------------------------------------------------------------------
  // API CALCULATIONS (Active Pharmaceutical Ingredient)
  // --------------------------------------------------------------------------
  {
    id: 'api-stoich-yield',
    name: 'Stoichiometric Yield',
    scope: ['API'],
    category: 'Stoichiometry',
    evaluator: 'api.stoichYield', // Phase 4: Real evaluator
    outputUnit: '%',
    variables: [
      {
        name: 'limitingMol',
        label: 'Moles of limiting reactant',
        source: { type: 'manual' },
        unit: 'mol',
        required: true,
      },
      {
        name: 'stoichRatio',
        label: 'Stoichiometric ratio (product:limiting)',
        source: { type: 'manual' },
        required: false,
      },
      {
        name: 'productMW',
        label: 'Molar mass of product',
        source: { type: 'manual' },
        unit: 'g/mol',
        required: true,
      },
      {
        name: 'actualMass',
        label: 'Actual product mass',
        source: { type: 'manual' },
        unit: 'g',
        required: true,
      },
    ],
    // Legacy support
    formula: '((actualYield / theoreticalYield) * 100) || 0',
    inputs: [
      {
        id: 'actualYield',
        label: 'Actual Yield',
        source: { type: 'manual' },
      },
      {
        id: 'theoreticalYield',
        label: 'Theoretical Yield',
        source: { type: 'manual' },
      },
    ],
    description: 'Calculate percentage yield: (actualMass / (limitingMol × stoichRatio × productMW)) × 100',
  },
  {
    id: 'api-stoich-ratio',
    name: 'Stoichiometric Ratio',
    scope: ['API'],
    category: 'Stoichiometry',
    formula: '(reactantTotal / productTotal) || 0',
    outputUnit: ':1',
    inputs: [
      {
        id: 'reactantTotal',
        label: 'Total Reactants',
        source: { type: 'bomTotal', role: 'Reactant' },
      },
      {
        id: 'productTotal',
        label: 'Total Products',
        source: { type: 'bomTotal', role: 'Product' },
      },
    ],
    description: 'Molar ratio of reactants to products (from BOM)',
  },
  {
    id: 'api-green-mass-efficiency',
    name: 'Mass Efficiency',
    scope: ['API'],
    category: 'GreenChem',
    evaluator: 'api.greenMetric', // Phase 4: Real evaluator
    outputUnit: '%',
    variables: [
      {
        name: 'massProducts',
        label: 'Total Products Mass',
        source: {
          type: 'bomAggregate',
          role: 'Product',
          field: 'quantity',
          mode: 'sum',
        },
        unit: 'g',
        required: true,
      },
      {
        name: 'massAllMaterials',
        label: 'Total All Materials Mass',
        source: {
          type: 'bomAggregate',
          role: 'Reactant',
          field: 'quantity',
          mode: 'sum',
        },
        unit: 'g',
        required: false,
      },
    ],
    // Legacy support
    formula: '((productTotal / (reactantTotal + reagentTotal)) * 100) || 0',
    inputs: [
      {
        id: 'productTotal',
        label: 'Total Products',
        source: { type: 'bomTotal', role: 'Product' },
      },
      {
        id: 'reactantTotal',
        label: 'Total Reactants',
        source: { type: 'bomTotal', role: 'Reactant' },
      },
      {
        id: 'reagentTotal',
        label: 'Total Reagents',
        source: { type: 'bomTotal', role: 'Reagent' },
      },
    ],
    description: 'Green chemistry metric: (product mass / total input mass) × 100',
  },

  // --------------------------------------------------------------------------
  // FD CALCULATIONS (Final Drug)
  // --------------------------------------------------------------------------
  {
    id: 'fd-assay-dried',
    name: 'Assay on Dried Basis',
    scope: ['FD'],
    category: 'Assay',
    evaluator: 'fd.assayBasis', // Phase 4: Real evaluator
    outputUnit: '%',
    variables: [
      {
        name: 'assayAsIs',
        label: 'Assay (as is)',
        source: { type: 'manual' },
        unit: '%',
        required: true,
      },
      {
        name: 'LOD',
        label: 'Loss on Drying',
        source: { type: 'manual' },
        unit: '%',
        required: true,
      },
    ],
    // Legacy support
    formula: '((assayAsIs / (1 - moisture / 100)) * 100) || 0',
    inputs: [
      {
        id: 'assayAsIs',
        label: 'Assay (as is)',
        source: { type: 'manual' },
      },
      {
        id: 'moisture',
        label: 'Moisture Content',
        source: { type: 'manual' },
      },
    ],
    description: 'Assay corrected for moisture content: (assayAsIs × 100) / (100 - LOD)',
  },
  {
    id: 'fd-assay-anhydrous',
    name: 'Assay on Anhydrous Basis',
    scope: ['FD'],
    category: 'Assay',
    formula: '((assayAsIs / (1 - waterContent / 100)) * 100) || 0',
    outputUnit: '%',
    inputs: [
      {
        id: 'assayAsIs',
        label: 'Assay (as is)',
        source: { type: 'manual' },
      },
      {
        id: 'waterContent',
        label: 'Water Content',
        source: { type: 'manual' },
      },
    ],
    description: 'Assay corrected for water content',
  },
  {
    id: 'fd-assay-theoretical',
    name: 'Assay on Theoretical Basis',
    scope: ['FD'],
    category: 'Assay',
    formula: '((measuredAmount / theoreticalAmount) * 100) || 0',
    outputUnit: '%',
    inputs: [
      {
        id: 'measuredAmount',
        label: 'Measured Amount',
        source: { type: 'manual' },
      },
      {
        id: 'theoreticalAmount',
        label: 'Theoretical Amount',
        source: { type: 'manual' },
      },
    ],
    description: 'Assay relative to theoretical maximum',
  },
  {
    id: 'fd-overage',
    name: 'Overage Calculation',
    scope: ['FD'],
    category: 'Overage',
    evaluator: 'fd.overage', // Phase 4: Real evaluator
    outputUnit: '%',
    variables: [
      {
        name: 'labelClaim',
        label: 'Label Claim',
        source: { type: 'manual' },
        unit: '%',
        required: true,
      },
      {
        name: 'actualAssay',
        label: 'Actual Assay',
        source: { type: 'manual' },
        unit: '%',
        required: true,
      },
    ],
    // Legacy support
    formula: '((targetAmount * (1 + overagePercent / 100)) - targetAmount) || 0',
    inputs: [
      {
        id: 'targetAmount',
        label: 'Target Amount',
        source: { type: 'manual' },
      },
      {
        id: 'overagePercent',
        label: 'Overage %',
        source: { type: 'manual' },
      },
    ],
    description: 'Calculate overage percentage: ((actual - labelClaim) / labelClaim) × 100',
  },

  // --------------------------------------------------------------------------
  // ADL CALCULATIONS (Analytical)
  // --------------------------------------------------------------------------
  {
    id: 'adl-lod',
    name: 'Loss on Drying (LOD)',
    scope: ['ADL'],
    category: 'LOD',
    evaluator: 'adl.lod', // Phase 4: Real evaluator
    outputUnit: '%',
    variables: [
      {
        name: 'initialWeight',
        label: 'Initial Weight',
        source: { type: 'manual' },
        unit: 'g',
        required: true,
      },
      {
        name: 'finalWeight',
        label: 'Final Weight',
        source: { type: 'manual' },
        unit: 'g',
        required: true,
      },
    ],
    // Legacy support
    formula: '(((weightBefore - weightAfter) / weightBefore) * 100) || 0',
    inputs: [
      {
        id: 'weightBefore',
        label: 'Weight Before Drying',
        source: { type: 'manual' },
      },
      {
        id: 'weightAfter',
        label: 'Weight After Drying',
        source: { type: 'manual' },
      },
    ],
    description: 'Calculate moisture loss: ((initial - final) / initial) × 100',
  },
  {
    id: 'adl-percent-variation',
    name: 'Percent Variation',
    scope: ['ADL'],
    category: 'Variation',
    evaluator: 'adl.variation', // Phase 4: Real evaluator
    outputUnit: '%',
    variables: [
      {
        name: 'value1',
        label: 'Value 1',
        source: { type: 'manual' },
        required: true,
      },
      {
        name: 'value2',
        label: 'Value 2',
        source: { type: 'manual' },
        required: true,
      },
    ],
    // Legacy support
    formula: '((Math.abs(value1 - value2) / ((value1 + value2) / 2)) * 100) || 0',
    inputs: [
      {
        id: 'value1',
        label: 'Value 1',
        source: { type: 'manual' },
      },
      {
        id: 'value2',
        label: 'Value 2',
        source: { type: 'manual' },
      },
    ],
    description: 'Percentage variation: ((value2 - value1) / value1) × 100',
  },
  {
    id: 'adl-rsd',
    name: 'Relative Standard Deviation (RSD)',
    scope: ['ADL'],
    category: 'Variation',
    formula: '((stdDev / mean) * 100) || 0',
    outputUnit: '%',
    inputs: [
      {
        id: 'mean',
        label: 'Mean Value',
        source: { type: 'manual' },
      },
      {
        id: 'stdDev',
        label: 'Standard Deviation',
        source: { type: 'manual' },
      },
    ],
    description: 'RSD for replicate measurements',
  },
  {
    id: 'adl-arithmetic-mean',
    name: 'Arithmetic Mean',
    scope: ['ADL'],
    category: 'Variation',
    formula: '((val1 + val2 + val3) / 3) || 0',
    outputUnit: '',
    inputs: [
      {
        id: 'val1',
        label: 'Value 1',
        source: { type: 'manual' },
      },
      {
        id: 'val2',
        label: 'Value 2',
        source: { type: 'manual' },
      },
      {
        id: 'val3',
        label: 'Value 3',
        source: { type: 'manual' },
      },
    ],
    description: 'Calculate arithmetic mean of three values',
  },
];

// ============================================================================
// STANDARD BOM FIELDS
// ============================================================================

/**
 * Base BOM fields common to all reaction types
 */
const standardBomFields: BomFieldDefinition[] = [
  {
    id: 'name',
    label: 'Material Name',
    type: 'string',
    required: true,
    placeholder: 'Enter material name',
  },
  {
    id: 'role',
    label: 'Role',
    type: 'select',
    required: true,
    options: ['Reactant', 'Reagent', 'Solvent', 'Product', 'Intermediate'],
  },
  {
    id: 'quantity',
    label: 'Quantity',
    type: 'number',
    required: true,
    placeholder: '0',
  },
  {
    id: 'unit',
    label: 'Unit',
    type: 'select',
    required: true,
    options: ['g', 'mg', 'kg', 'mL', 'L', 'mol', 'mmol', 'eq'],
  },
  {
    id: 'batchNo',
    label: 'Batch No',
    type: 'string',
    required: false,
    placeholder: 'Optional',
  },
];

// ============================================================================
// REACTION TEMPLATES
// ============================================================================

/**
 * Templates defining BOM fields and calculations for each reaction type
 */
export const reactionTemplates: ReactionTemplate[] = [
  {
    reactionType: 'Peptide',
    label: 'Peptide Synthesis',
    bomFields: [
      ...standardBomFields,
      {
        id: 'equivalents',
        label: 'Equivalents',
        type: 'number',
        required: false,
        placeholder: '1.0',
        visibleForRoles: ['Reactant', 'Reagent'],
      },
    ],
    enabledCalcIds: ['api-stoich-yield', 'api-stoich-ratio', 'api-green-mass-efficiency'],
  },
  {
    reactionType: 'Enzyme',
    label: 'Enzyme Reaction',
    bomFields: [
      ...standardBomFields,
      {
        id: 'enzymeUnits',
        label: 'Enzyme Units',
        type: 'number',
        required: false,
        placeholder: '0',
        visibleForRoles: ['Reagent'],
      },
    ],
    enabledCalcIds: ['api-stoich-yield', 'api-stoich-ratio'],
  },
  {
    reactionType: 'FlowChemistry',
    label: 'Flow Chemistry',
    bomFields: [
      ...standardBomFields,
      {
        id: 'flowRate',
        label: 'Flow Rate (mL/min)',
        type: 'number',
        required: false,
        placeholder: '0',
      },
      {
        id: 'residenceTime',
        label: 'Residence Time (min)',
        type: 'number',
        required: false,
        placeholder: '0',
      },
    ],
    enabledCalcIds: ['api-stoich-yield', 'api-green-mass-efficiency'],
  },
  {
    reactionType: 'Lyophilisation',
    label: 'Lyophilisation (Freeze Drying)',
    bomFields: [
      ...standardBomFields,
      {
        id: 'initialVolume',
        label: 'Initial Volume (mL)',
        type: 'number',
        required: false,
        placeholder: '0',
      },
      {
        id: 'finalWeight',
        label: 'Final Weight (g)',
        type: 'number',
        required: false,
        placeholder: '0',
      },
    ],
    enabledCalcIds: ['fd-assay-dried', 'fd-overage', 'adl-lod'],
  },
  {
    reactionType: 'Chromatography',
    label: 'Chromatography Purification',
    bomFields: [
      ...standardBomFields,
      {
        id: 'columnVolume',
        label: 'Column Volume (mL)',
        type: 'number',
        required: false,
        placeholder: '0',
      },
      {
        id: 'elutionVolume',
        label: 'Elution Volume (mL)',
        type: 'number',
        required: false,
        placeholder: '0',
      },
    ],
    enabledCalcIds: ['fd-assay-dried', 'fd-assay-anhydrous', 'api-stoich-yield'],
  },
  {
    reactionType: 'PhiTec',
    label: 'Phi-Tec Process',
    bomFields: standardBomFields,
    enabledCalcIds: ['api-stoich-yield', 'fd-assay-theoretical'],
  },
  {
    reactionType: 'Polymorphism',
    label: 'Polymorphism Study',
    bomFields: [
      ...standardBomFields,
      {
        id: 'polymorphForm',
        label: 'Polymorph Form',
        type: 'string',
        required: false,
        placeholder: 'e.g., Form I',
      },
    ],
    enabledCalcIds: ['fd-assay-dried', 'fd-assay-anhydrous', 'adl-percent-variation'],
  },
  {
    reactionType: 'Premix',
    label: 'Premix Formulation',
    bomFields: [
      ...standardBomFields,
      {
        id: 'mixingTime',
        label: 'Mixing Time (min)',
        type: 'number',
        required: false,
        placeholder: '0',
      },
    ],
    enabledCalcIds: ['fd-overage', 'adl-arithmetic-mean'],
  },
  {
    reactionType: 'SprayDrying',
    label: 'Spray Drying',
    bomFields: [
      ...standardBomFields,
      {
        id: 'inletTemp',
        label: 'Inlet Temp (C)',
        type: 'number',
        required: false,
        placeholder: '0',
      },
      {
        id: 'outletTemp',
        label: 'Outlet Temp (C)',
        type: 'number',
        required: false,
        placeholder: '0',
      },
    ],
    enabledCalcIds: ['fd-assay-dried', 'adl-lod', 'fd-overage'],
  },
  {
    reactionType: 'TSU',
    label: 'TSU Process',
    bomFields: standardBomFields,
    enabledCalcIds: ['api-stoich-yield', 'fd-assay-theoretical', 'adl-rsd'],
  },
];

// ============================================================================
// PLUGIN CONFIGURATION EXPORT
// ============================================================================

/**
 * Complete plugin configuration
 * Import this in components that need calculation or template data
 */
export const chemConfig: ChemConfig = {
  calculations,
  reactionTemplates,
};

/**
 * Helper functions for config lookups
 */
export const getReactionTemplate = (reactionType: string) => {
  return reactionTemplates.find((t) => t.reactionType === reactionType);
};

export const getCalculation = (calcId: string) => {
  return calculations.find((c) => c.id === calcId);
};

export const getCalculationsForExperiment = (
  experimentType: string,
  reactionType: string
) => {
  const template = getReactionTemplate(reactionType);
  if (!template) return [];

  return calculations.filter(
    (calc) =>
      calc.scope.includes(experimentType as any) &&
      template.enabledCalcIds.includes(calc.id)
  );
};

